/**
 * Product builder — parse parenthetical option labels; show note as tag above button.
 */
(function (global) {
  'use strict';

  var TRAILING_PAREN_RE = /^(.+?)\s*\(([^)]+)\)\s*$/;

  function parseOptionLabel(text) {
    var raw = typeof text === 'string' ? text.replace(/\s+/g, ' ').trim() : '';
    if (!raw) return { primary: '', secondary: '', full: '' };
    var match = raw.match(TRAILING_PAREN_RE);
    if (!match) return { primary: raw, secondary: '', full: raw };
    var primary = match[1].trim();
    var secondary = match[2].trim();
    return { primary: primary, secondary: secondary, full: raw };
  }

  function formatOptionLabel(primary, secondary) {
    var p = (primary || '').trim();
    var s = (secondary || '').trim();
    if (!p) return s;
    if (!s) return p;
    return p + ' (' + s + ')';
  }

  /** Tag row above button: optional price delta + optional note, e.g. "+₱200 · Best Seller". */
  function formatOptionMetaLine(priceText, noteText) {
    var price = (priceText || '').trim();
    var note = (noteText || '').trim();
    if (price && note) return price + ' \u00b7 ' + note;
    return price || note || '';
  }

  function getButtonLabelSource(btn) {
    if (!btn || !btn.dataset) return '';
    return (btn.dataset.pbTemplate || btn.dataset.value || '').trim();
  }

  function isChoiceButton(btn) {
    if (!btn || !btn.classList) return false;
    if (btn.classList.contains('pb-ai-image-retry')) return false;
    if (btn.classList.contains('pb-character-regen')) return false;
    return btn.classList.contains('variant-btn') || btn.classList.contains('meta-btn');
  }

  function getChoiceButtons(group) {
    var cells = group.querySelectorAll(':scope > .pb-option-cell');
    if (cells.length) {
      return Array.from(cells)
        .map(function (cell) { return cell.querySelector('.builder-btn'); })
        .filter(function (btn) { return btn && isChoiceButton(btn); });
    }
    return Array.from(group.querySelectorAll('.builder-btn')).filter(isChoiceButton);
  }

  function readButtonLabel(btn) {
    var fromData = getButtonLabelSource(btn);
    if (fromData) return fromData;
    return (btn.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function flattenButtonToPrimary(btn, primary) {
    btn.classList.remove('pb-option-has-sublabels', 'pb-variant-has-pricing');
    var labelEl = btn.querySelector('.pb-variant-label');
    var deltaEl = btn.querySelector('.pb-variant-price-delta');
    if (labelEl && deltaEl) {
      labelEl.textContent = primary;
      return;
    }
    btn.textContent = primary;
  }

  function ensureCellWrap(btn) {
    if (btn.parentElement && btn.parentElement.classList.contains('pb-option-cell')) {
      return btn.parentElement;
    }
    var cell = document.createElement('div');
    cell.className = 'pb-option-cell';
    btn.parentElement.insertBefore(cell, btn);
    cell.appendChild(btn);
    return cell;
  }

  function ensureTag(cell, text) {
    var tag = cell.querySelector(':scope > .pb-option-tag');
    if (!tag) {
      tag = document.createElement('span');
      tag.className = 'pb-option-tag';
      cell.insertBefore(tag, cell.firstChild);
    }
    tag.textContent = text || '';
    if (!text) tag.setAttribute('aria-hidden', 'true');
    else tag.removeAttribute('aria-hidden');
    return tag;
  }

  function unwrapCell(cell) {
    var btn = cell.querySelector('.builder-btn');
    if (!btn || !cell.parentElement) return;
    cell.parentElement.insertBefore(btn, cell);
    cell.remove();
  }

  function removeGroupEnhancement(group) {
    group.removeAttribute('data-pb-has-sublabels');
    getChoiceButtons(group).forEach(function (btn) {
      btn.classList.remove('pb-option-has-sublabels', 'pb-variant-has-pricing');
      btn.textContent = readButtonLabel(btn);
    });
    Array.from(group.querySelectorAll(':scope > .pb-option-cell')).forEach(unwrapCell);
  }

  function hasPriceMeta(buttons) {
    return buttons.some(function (btn) {
      return !!((btn.dataset && btn.dataset.pbPriceDelta) || '').trim();
    });
  }

  function enhanceGroup(group) {
    var buttons = Array.from(group.querySelectorAll('.builder-btn')).filter(isChoiceButton);
    if (!buttons.length) return;

    var parsedList = buttons.map(function (btn) {
      return parseOptionLabel(readButtonLabel(btn));
    });
    var hasAnySecondary = parsedList.some(function (p) { return !!p.secondary; });

    if (!hasAnySecondary) {
      if (hasPriceMeta(buttons)) return;
      removeGroupEnhancement(group);
      return;
    }

    group.setAttribute('data-pb-has-sublabels', 'true');
    buttons.forEach(function (btn, i) {
      var parsed = parsedList[i];
      var cell = ensureCellWrap(btn);
      ensureTag(cell, parsed.secondary || '');
      flattenButtonToPrimary(btn, parsed.primary);
    });
  }

  function tagHasVisibleText(tag) {
    return !!(tag && tag.textContent.trim() && tag.getAttribute('aria-hidden') !== 'true');
  }

  function groupCellsByRow(cells) {
    var rows = [];
    cells.forEach(function (cell) {
      if (!cell.offsetParent) return;
      var top = cell.offsetTop;
      var row = null;
      for (var i = 0; i < rows.length; i++) {
        if (Math.abs(rows[i].top - top) <= 2) {
          row = rows[i];
          break;
        }
      }
      if (!row) {
        row = { top: top, cells: [] };
        rows.push(row);
      }
      row.cells.push(cell);
    });
    return rows.map(function (r) { return r.cells; });
  }

  function syncTagRows(group) {
    if (!group || group.getAttribute('data-pb-has-sublabels') !== 'true') return;
    syncMetaRows(group);
  }

  function syncMetaRows(group) {
    if (!group || group.getAttribute('data-pb-has-sublabels') !== 'true') return;
    var cells = Array.from(group.querySelectorAll(':scope > .pb-option-cell'));
    if (!cells.length) return;

    groupCellsByRow(cells).forEach(function (rowCells) {
      var rowHasTag = rowCells.some(function (cell) {
        var btn = cell.querySelector('.builder-btn');
        if (!btn) return false;
        var parsed = parseOptionLabel(readButtonLabel(btn));
        var metaLine = formatOptionMetaLine(btn.dataset.pbPriceDelta || '', parsed.secondary);
        return !!metaLine;
      });

      rowCells.forEach(function (cell) {
        var btn = cell.querySelector('.builder-btn');
        if (!btn) return;
        var parsed = parseOptionLabel(readButtonLabel(btn));
        var metaLine = formatOptionMetaLine(btn.dataset.pbPriceDelta || '', parsed.secondary);

        if (rowHasTag) {
          cell.classList.remove('pb-option-row-no-tag');
          ensureTag(cell, metaLine);
        } else {
          cell.classList.add('pb-option-row-no-tag');
        }
      });
    });
  }

  function syncTagRowsInRoot(root) {
    if (!root) return;
    root.querySelectorAll('.variant-slider-track[data-pb-has-sublabels="true"], .btn-grid[data-pb-has-sublabels="true"]').forEach(syncTagRows);
  }

  function enhance(root) {
    if (!root) return;
    var groups = root.querySelectorAll('.variant-slider-track, .btn-grid');
    groups.forEach(enhanceGroup);
  }

  var api = {
    parseOptionLabel: parseOptionLabel,
    formatOptionLabel: formatOptionLabel,
    formatOptionMetaLine: formatOptionMetaLine,
    getButtonLabelSource: getButtonLabelSource,
    ensureCellWrap: ensureCellWrap,
    ensureTag: ensureTag,
    flattenButtonToPrimary: flattenButtonToPrimary,
    enhance: enhance,
    enhanceGroup: enhanceGroup,
    syncTagRows: syncTagRows,
    syncMetaRows: syncMetaRows,
    syncTagRowsInRoot: syncTagRowsInRoot,
    groupCellsByRow: groupCellsByRow
  };

  global.PBOptionLabel = api;

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
})(typeof window !== 'undefined' ? window : globalThis);
